import { useState } from "react";
import "./IssueEdit.css";
import { Button } from "./Button";
import { Alert } from "./Card";
import { Input, Select } from "./Field";
import { StatusPill } from "./Status";
import type { Complexity, Issue, IssueActsApi, IssueType, OpenQuestion, Priority } from "../lib/tasks";
import { errText, issueActsApi } from "../lib/tasks";
import { complexityText, priorityText } from "./IssueCard";
import { tx } from "../i18n";

/* ══ 이슈 상세의 고치는 자리 ══
   채택 시안 Figma 31:2(t-in-issue-acts-2) — 스펙 편집 · 열린 질문 · 막힘 보완 · 선행 · 하위.
   b3x-pr 상세에서 옮겼다. **판정은 서버가 한다** — 여기는 부르고, 거절은 서버 문장을 그대로
   보인다. 부품마다 실패를 자기 자리에 적는다(한 곳에 모으면 무엇이 실패했는지 흐려진다). */

const TYPES: IssueType[] = ["feature", "bug", "chore", "design", "plan"];
const PRIORITIES: Priority[] = ["critical", "high", "normal"];
const COMPLEXITIES: Complexity[] = ["deep", "hard", "normal", "light"];

function useAct() {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  async function act(key: string, f: () => Promise<unknown>, after?: () => void) {
    setBusy(key);
    setErr(null);
    try {
      await f();
      after?.();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(null);
    }
  }
  return { busy, err, act };
}

/** 스펙 편집 — 제목 · 칩(유형 · 우선순위 · 복잡도) · 의도 · 수용 기준. **바뀐 칸만** 보낸다 —
 *  안 건드린 칸까지 보내면 그사이 세션이 채운 값을 사람이 모르고 덮는다. */
export function IssueSpecEditor({
  t,
  api = issueActsApi,
  onSaved,
  onClose,
}: {
  t: Issue;
  api?: IssueActsApi;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(t.title);
  const [type, setType] = useState<IssueType | "">(t.type ?? "");
  const [priority, setPriority] = useState(t.priority);
  const [complexity, setComplexity] = useState(t.complexity);
  const [intent, setIntent] = useState(t.intent);
  const [acc, setAcc] = useState(t.acceptance.join("\n"));
  const { busy, err, act } = useAct();

  const acceptance = acc
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const body: Parameters<IssueActsApi["patch"]>[1] = {};
  if (title.trim() !== t.title) body.title = title.trim();
  if (type !== (t.type ?? "")) body.type = type;
  if (priority !== t.priority) body.priority = priority;
  if (complexity !== t.complexity) body.complexity = complexity;
  if (intent !== t.intent) body.intent = intent;
  if (acceptance.join("\n") !== t.acceptance.join("\n")) body.acceptance = acceptance;
  const dirty = Object.keys(body).length > 0;

  // 타입 칸에 시스템 유형(마법사 · 정리 · 서버 제어)이 이미 있으면 목록에 그대로 둔다 — 지우면 값이 바뀐다.
  const types = t.type && !TYPES.includes(t.type) ? [...TYPES, t.type] : TYPES;

  return (
    <form
      className="iedit"
      aria-label={tx("issueEdit.label")}
      onSubmit={(e) => {
        e.preventDefault();
        if (dirty && title.trim()) act("save", () => api.patch(t.id, body), onSaved);
      }}
    >
      <label className="iedit__f">
        <span>{tx("issueEdit.title")}</span>
        <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <div className="iedit__chips">
        <label className="iedit__f">
          <span>{tx("issueEdit.type")}</span>
          <Select size="md" value={type} onChange={(e) => setType(e.target.value as IssueType | "")}>
            <option value="">{tx("issue.noType")}</option>
            {types.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </label>
        <label className="iedit__f">
          <span>{tx("issueEdit.priority")}</span>
          <Select size="md" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
            {PRIORITIES.map((v) => (
              <option key={v} value={v}>
                {priorityText(v)}
              </option>
            ))}
          </Select>
        </label>
        <label className="iedit__f">
          <span>{tx("issueEdit.complexity")}</span>
          <Select size="md" value={complexity} onChange={(e) => setComplexity(e.target.value as Complexity)}>
            {COMPLEXITIES.map((v) => (
              <option key={v} value={v}>
                {complexityText(v)}
              </option>
            ))}
          </Select>
        </label>
      </div>
      <label className="iedit__f">
        <span>{tx("board.detail.intent")}</span>
        <textarea className="b-textarea" rows={3} value={intent} onChange={(e) => setIntent(e.target.value)} />
      </label>
      <label className="iedit__f">
        <span>{tx("issueEdit.acceptance")}</span>
        <textarea className="b-textarea" rows={4} value={acc} onChange={(e) => setAcc(e.target.value)} />
      </label>
      {err ? <Alert tone="error">{err}</Alert> : null}
      <div className="iedit__row">
        <Button type="button" onClick={onClose} disabled={busy !== null}>
          {tx("common.action.close")}
        </Button>
        <Button type="submit" variant="primary" loading={busy === "save"} disabled={!dirty || !title.trim()}>
          {tx("common.action.save")}
        </Button>
      </div>
    </form>
  );
}

/** 열린 질문 — 보기 단추 또는 직접 적어 답한다. 답하면 서버가 물을 때의 단계로 돌린다. */
export function IssueQuestions({
  t,
  api = issueActsApi,
  onChanged,
}: {
  t: Issue;
  api?: IssueActsApi;
  onChanged: () => void;
}) {
  const qs = t.openQuestions ?? [];
  if (qs.length === 0) return null;
  return (
    <section className="iedit__card is-warn" aria-label={tx("issueEdit.questions", { n: qs.length })}>
      <h3>{tx("issueEdit.questions", { n: qs.length })}</h3>
      {qs.map((q) => (
        <QuestionRow key={q.id} t={t} q={q} api={api} onChanged={onChanged} />
      ))}
    </section>
  );
}

function QuestionRow({ t, q, api, onChanged }: { t: Issue; q: OpenQuestion; api: IssueActsApi; onChanged: () => void }) {
  const [text, setText] = useState("");
  const { busy, err, act } = useAct();
  const answer = (a: string) => act(`a:${a}`, () => api.answer(t.id, q.id, a), onChanged);
  return (
    <div className="iedit__q">
      <p>{q.prompt}</p>
      {q.options?.length ? (
        <div className="iedit__row">
          {q.options.map((o) => (
            <Button key={o} size="sm" loading={busy === `a:${o}`} disabled={busy !== null} onClick={() => answer(o)}>
              {o}
            </Button>
          ))}
        </div>
      ) : null}
      <div className="iedit__row">
        <Input
          size="md"
          aria-label={tx("issueEdit.answerPlaceholder")}
          placeholder={tx("issueEdit.answerPlaceholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button
          size="md"
          variant="primary"
          loading={busy === `a:${text.trim()}`}
          disabled={busy !== null || !text.trim()}
          onClick={() => answer(text.trim())}
        >
          {tx("issueEdit.answer")}
        </Button>
      </div>
      {err ? <Alert tone="error">{err}</Alert> : null}
    </div>
  );
}

/** 막힘 보완 — 막힘이고 열린 질문이 없을 때만 선다(질문이 있으면 답하는 것이 먼저다). */
export function SupplementCard({
  t,
  api = issueActsApi,
  onChanged,
}: {
  t: Issue;
  api?: IssueActsApi;
  onChanged: () => void;
}) {
  const [note, setNote] = useState("");
  const { busy, err, act } = useAct();
  if (t.status !== "blocked" || (t.openQuestions?.length ?? 0) > 0 || t.childCount > 0) return null;
  return (
    <section className="iedit__card" aria-label={tx("issueEdit.supplement")}>
      <h3>{tx("issueEdit.supplement")}</h3>
      <p className="muted">{tx("issueEdit.supplementBody")}</p>
      <textarea
        className="b-textarea"
        rows={3}
        aria-label={tx("issueEdit.supplementPlaceholder")}
        placeholder={tx("issueEdit.supplementPlaceholder")}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {err ? <Alert tone="error">{err}</Alert> : null}
      <div className="iedit__row">
        <Button
          variant="primary"
          loading={busy === "sup"}
          disabled={busy !== null || !note.trim() || t.openSession !== null}
          onClick={() => act("sup", () => api.supplement(t.id, note.trim()), onChanged)}
        >
          {tx("issueEdit.supplementGo")}
        </Button>
      </div>
    </section>
  );
}

/** 선행 — 줄마다 ✕ 로 풀고, id 로 더한다. 이름 · 단계는 보드가 이미 읽은 목록에서 찾는다
 *  (없으면 id 만 — 다른 프로젝트일 수 있다). 고리 · 없는 id 는 서버가 거절한다. */
export function DepsEditor({
  t,
  all,
  api = issueActsApi,
  onChanged,
}: {
  t: Issue;
  all: Issue[];
  api?: IssueActsApi;
  onChanged: () => void;
}) {
  const [add, setAdd] = useState("");
  const { busy, err, act } = useAct();
  const byId = new Map(all.map((x) => [x.id, x]));
  return (
    <section className="iedit__card" aria-label={tx("issueEdit.deps", { n: t.dependsOn.length })}>
      <h3>{tx("issueEdit.deps", { n: t.dependsOn.length })}</h3>
      {t.dependsOn.length ? (
        <ul className="iedit__list">
          {t.dependsOn.map((d) => {
            const x = byId.get(d);
            return (
              <li key={d}>
                {x ? <StatusPill status={x.status} /> : null}
                <span className="iedit__name">{x ? x.title : d}</span>
                <button
                  type="button"
                  className="b-ibtn b-ibtn-sm b-btn-ghost"
                  aria-label={tx("issueEdit.depRemove", { id: d })}
                  title={tx("issueEdit.depRemove", { id: d })}
                  disabled={busy !== null}
                  onClick={() => act(`rm:${d}`, () => api.removeDep(t.id, d), onChanged)}
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="muted">{tx("common.state.none")}</p>
      )}
      <div className="iedit__row">
        <Input
          size="md"
          list={`deps-${t.id}`}
          aria-label={tx("issueEdit.depPlaceholder")}
          placeholder={tx("issueEdit.depPlaceholder")}
          value={add}
          onChange={(e) => setAdd(e.target.value)}
        />
        <datalist id={`deps-${t.id}`}>
          {all
            .filter((x) => x.id !== t.id && !t.dependsOn.includes(x.id))
            .slice(0, 200)
            .map((x) => (
              <option key={x.id} value={x.id}>
                {x.title}
              </option>
            ))}
        </datalist>
        <Button
          size="md"
          loading={busy === "add"}
          disabled={busy !== null || !add.trim()}
          onClick={() => act("add", () => api.patch(t.id, { dependsOn: [add.trim()] }), () => {
            setAdd("");
            onChanged();
          })}
        >
          {tx("issueEdit.depAdd")}
        </Button>
      </div>
      {err ? <Alert tone="error">{err}</Alert> : null}
    </section>
  );
}

/** 하위 — 목록과 「하위 추가」. 프로젝트는 상위의 것을 적는다(서버는 안 물려준다). */
export function ChildrenPanel({
  t,
  all,
  api = issueActsApi,
  onChanged,
  onOpen,
}: {
  t: Issue;
  all: Issue[];
  api?: IssueActsApi;
  onChanged: () => void;
  onOpen?: (id: string) => void;
}) {
  const [title, setTitle] = useState("");
  const { busy, err, act } = useAct();
  const kids = all.filter((x) => x.parentId === t.id);
  if (t.status === "done" || t.status === "cancelled") {
    if (kids.length === 0) return null;
  }
  return (
    <section className="iedit__card" aria-label={tx("issueEdit.children", { n: kids.length })}>
      <h3>{tx("issueEdit.children", { n: kids.length })}</h3>
      {kids.length ? (
        <ul className="iedit__list">
          {kids.map((k) => (
            <li key={k.id}>
              <StatusPill status={k.status} />
              {onOpen ? (
                <button type="button" className="iedit__link" onClick={() => onOpen(k.id)}>
                  {k.title}
                </button>
              ) : (
                <span className="iedit__name">{k.title}</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="iedit__row">
        <Input
          size="md"
          aria-label={tx("issueEdit.childPlaceholder")}
          placeholder={tx("issueEdit.childPlaceholder")}
          value={title}
          maxLength={200}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Button
          size="md"
          loading={busy === "child"}
          disabled={busy !== null || !title.trim()}
          onClick={() =>
            act("child", () => api.createChild(t.id, title.trim(), t.projectId), () => {
              setTitle("");
              onChanged();
            })
          }
        >
          {tx("issueEdit.childAdd")}
        </Button>
      </div>
      {err ? <Alert tone="error">{err}</Alert> : null}
    </section>
  );
}
